import {
  DOC_SYNC_FAILURE_CLASSES,
  type DocSyncFailureClass,
} from '@streamlain/doc-sync-taxonomy';

/** Marker-only diagnostic build: no row data, credentials or error messages. */
export const IMPORT_SEAM_MARK = 'sl-db-import-seam';
export const IMPORT_SEAM_MARK_LIMIT = 4096;
type Seam =
  | 'import.final-flush'
  | 'import.final-workspace-flush'
  | 'import.server-confirmation'
  | 'import.parser-dispose'
  | 'client.flush-rpc'
  | 'frontend.wait-for-updated'
  | 'frontend.flush-rpc'
  | 'projection.wait-for-updated'
  | 'projection.worker-rpc'
  | 'peer.connections'
  | 'peer.read-captured-doc'
  | 'peer.read-pushed-clock'
  | 'peer.clock'
  | 'peer.boundary';
type ClockState = {
  peer?: number;
  activeJob?: string | null;
  activeDocMatches?: boolean;
  queuedDocCount?: number;
  capturedClock?: number | null;
  pushedClock?: number | null;
  queuedJobs?: string[];
  isolatedFailureClass?: DocSyncFailureClass | null;
};
let sequence = 0;
let count = 0;

export function markImportSeam(
  stage: Seam,
  edge: 'start' | 'end' | 'error' | 'state',
  docId?: string,
  state: ClockState = {},
  span?: number,
  failureClass?: string
) {
  try {
    if (count > IMPORT_SEAM_MARK_LIMIT) return;
    if (count++ === IMPORT_SEAM_MARK_LIMIT) {
      performance.mark(IMPORT_SEAM_MARK, { detail: { truncated: true } });
      return;
    }
    const detail: Record<string, unknown> = { stage, edge, span };
    if (Number.isSafeInteger(state.peer) && state.peer! > 0)
      detail.peer = state.peer;
    if (state.activeJob === null) detail.activeJob = null;
    else if (state.activeJob !== undefined)
      detail.activeJob = [
        'connect',
        'push',
        'pull',
        'pullAndPush',
        'save',
      ].includes(state.activeJob)
        ? state.activeJob
        : 'other';
    if (typeof state.activeDocMatches === 'boolean')
      detail.activeDocMatches = state.activeDocMatches;
    if (
      Number.isSafeInteger(state.queuedDocCount) &&
      state.queuedDocCount! >= 0
    )
      detail.queuedDocCount = state.queuedDocCount;
    if (docId && /^[\w$:.\-]{1,256}$/.test(docId)) detail.docId = docId;
    for (const key of ['capturedClock', 'pushedClock'] as const) {
      const value = state[key];
      if (value === null || Number.isFinite(value)) detail[key] = value;
    }
    if (state.queuedJobs)
      detail.queuedJobs = state.queuedJobs
        .slice(0, 32)
        .map(type =>
          ['connect', 'push', 'pull', 'pullAndPush', 'save'].includes(type)
            ? type
            : 'other'
        );
    if (
      state.isolatedFailureClass === null ||
      DOC_SYNC_FAILURE_CLASSES.includes(state.isolatedFailureClass!)
    )
      detail.isolatedFailureClass = state.isolatedFailureClass;
    if (failureClass)
      detail.failureClass = [
        'Error',
        'AbortError',
        'TimeoutError',
        'TypeError',
        'RangeError',
      ].includes(failureClass)
        ? failureClass
        : 'OtherError';
    performance.mark(IMPORT_SEAM_MARK, { detail });
  } catch {
    // Diagnostic availability must never change the operation's result.
  }
}

export async function traceImportAwait<T>(
  stage: Seam,
  docId: string | undefined,
  operation: () => Promise<T>,
  readWorker?: ImportSeamReader
): Promise<T> {
  const span = ++sequence;
  markImportSeam(stage, 'start', docId, {}, span);
  const stopReadout = readWorker
    ? mirrorImportSeams(readWorker, docId, span)
    : undefined;
  try {
    const result = await operation();
    markImportSeam(stage, 'end', docId, {}, span);
    return result;
  } catch (error) {
    let failureClass = 'OtherError';
    try {
      if (error instanceof Error) failureClass = error.name;
    } catch {
      /* no diagnostic getter may replace the real failure */
    }
    markImportSeam(stage, 'error', docId, {}, span, failureClass);
    throw error;
  } finally {
    stopReadout?.();
  }
}

export const IMPORT_SHARED_SEAM_MARK = 'sl-db-import-shared-seam';
export const IMPORT_SHARED_READOUT_MARK = 'sl-db-import-shared-readout';
export type ImportSeamReadout = {
  timeOrigin: number;
  capturedAtUnixMs: number;
  nextOffset: number;
  totalCount: number;
  entries: { startTime: number; detail: Record<string, unknown> }[];
};
export type ImportSeamReader = (
  offset: number,
  signal: AbortSignal
) => Promise<ImportSeamReadout>;
const detailKeys = [
  'peer',
  'activeJob',
  'activeDocMatches',
  'queuedDocCount',
  'stage',
  'edge',
  'span',
  'docId',
  'capturedClock',
  'pushedClock',
  'queuedJobs',
  'isolatedFailureClass',
  'failureClass',
  'truncated',
] as const;

/** Bounded read-only port operation. Reads this worker's existing marks only. */
export function readImportSeams(
  offset: number,
  docId: string
): ImportSeamReadout {
  if (!/^[\w$:.\-]{1,256}$/.test(docId))
    throw new RangeError('Invalid diagnostic document');
  if (
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    offset > IMPORT_SEAM_MARK_LIMIT + 1
  )
    throw new RangeError('Invalid diagnostic offset');
  const marks = performance
    .getEntriesByName(IMPORT_SEAM_MARK)
    .slice(0, IMPORT_SEAM_MARK_LIMIT + 1);
  const entries = marks.slice(offset, offset + 128).map(mark => ({
    startTime: mark.startTime,
    detail: Object.fromEntries(
      detailKeys
        .filter(key => (mark as PerformanceMark).detail?.[key] !== undefined)
        .map(key => [key, (mark as PerformanceMark).detail[key]])
    ),
  }));
  return {
    timeOrigin: performance.timeOrigin,
    capturedAtUnixMs: performance.timeOrigin + performance.now(),
    totalCount: marks.length,
    nextOffset: Math.min(marks.length, offset + entries.length),
    entries: entries.filter(
      entry => entry.detail.docId === docId || entry.detail.truncated
    ),
  };
}

type CaptureScope = { docId?: string; span: number; stoppedAt?: number };
type MirrorState = {
  offset: number;
  reading: boolean;
  timer?: ReturnType<typeof setTimeout>;
  realm: number;
  scopes: Map<number, CaptureScope>;
};
const mirrors = new WeakMap<ImportSeamReader, MirrorState>();
let mirrorRealm = 0;
let mirroredCount = 0;
let readoutCount = 0;
function diagnosticNow() {
  try {
    const value = performance.timeOrigin + performance.now();
    if (Number.isFinite(value)) return value;
  } catch {
    /* availability cannot change the real operation */
  }
  return Date.now();
}
function readoutMark(
  scope: CaptureScope,
  realm: number,
  readState: string,
  capturedAtUnixMs?: number
) {
  try {
    if (readoutCount > IMPORT_SEAM_MARK_LIMIT) return;
    if (readoutCount++ === IMPORT_SEAM_MARK_LIMIT) {
      performance.mark(IMPORT_SHARED_READOUT_MARK, {
        detail: { truncated: true },
      });
      return;
    }
    const detail: Record<string, unknown> = {
      clientSpan: scope.span,
      syncRealm: realm,
      readState,
      capturedAtUnixMs,
    };
    if (scope.docId && /^[\w$:.\-]{1,256}$/.test(scope.docId))
      detail.docId = scope.docId;
    performance.mark(IMPORT_SHARED_READOUT_MARK, { detail });
  } catch {
    /* diagnostic reporting never replaces the operation */
  }
}

/** Observe the actual pending flush over its existing port; never await in CRUD. */
export function mirrorImportSeams(
  reader: ImportSeamReader,
  docId: string | undefined,
  span: number
): () => void {
  let state = mirrors.get(reader);
  if (!state) {
    state = {
      offset: 0,
      reading: false,
      realm: ++mirrorRealm,
      scopes: new Map(),
    };
    mirrors.set(reader, state);
  }
  const current = state;
  const scope: CaptureScope = { docId, span };
  if (current.scopes.size >= 32) {
    readoutMark(scope, current.realm, 'error');
    return () => {};
  }
  current.scopes.set(span, scope);
  readoutMark(scope, current.realm, 'start');
  const read = async () => {
    if (current.reading || !current.scopes.size) return;
    current.reading = true;
    const abort = new AbortController();
    const deadline = setTimeout(() => abort.abort(), 2000);
    let latest: ImportSeamReadout | undefined;
    try {
      // At most four 128-mark pages per tick; an unresponsive port is cancelled.
      for (let page = 0; page < 4; page++) {
        const batch = await reader(current.offset, abort.signal);
        if (
          !Number.isFinite(batch.timeOrigin) ||
          !Number.isFinite(batch.capturedAtUnixMs) ||
          !Number.isSafeInteger(batch.nextOffset) ||
          batch.nextOffset < current.offset ||
          batch.nextOffset > IMPORT_SEAM_MARK_LIMIT + 1 ||
          batch.entries.length > 128
        )
          throw new Error('Invalid diagnostic page');
        for (const entry of batch.entries) {
          if (mirroredCount > IMPORT_SEAM_MARK_LIMIT) break;
          if (mirroredCount++ === IMPORT_SEAM_MARK_LIMIT) {
            performance.mark(IMPORT_SHARED_SEAM_MARK, {
              detail: { truncated: true },
            });
            break;
          }
          performance.mark(IMPORT_SHARED_SEAM_MARK, {
            detail: {
              ...Object.fromEntries(
                detailKeys
                  .filter(key => entry.detail[key] !== undefined)
                  .map(key => [key, entry.detail[key]])
              ),
              sourceUnixMs: batch.timeOrigin + entry.startTime,
              syncRealm: current.realm,
            },
          });
        }
        const advanced = batch.nextOffset > current.offset;
        current.offset = batch.nextOffset;
        latest = batch;
        if (
          !advanced ||
          current.offset >= batch.totalCount ||
          abort.signal.aborted
        )
          break;
      }
      const caughtUp = latest && current.offset >= latest.totalCount;
      for (const [id, observer] of current.scopes) {
        readoutMark(
          observer,
          current.realm,
          caughtUp ? 'caught-up' : 'partial',
          latest?.capturedAtUnixMs
        );
        if (
          caughtUp &&
          observer.stoppedAt !== undefined &&
          latest!.capturedAtUnixMs >= observer.stoppedAt
        )
          current.scopes.delete(id);
      }
    } catch {
      // Payload-free failure evidence is visible, independent of the real flush.
      for (const [id, observer] of current.scopes) {
        readoutMark(observer, current.realm, 'error');
        if (observer.stoppedAt !== undefined) current.scopes.delete(id);
      }
    } finally {
      clearTimeout(deadline);
      current.reading = false;
      if (current.scopes.size) {
        const finalCapture = [...current.scopes.values()].some(
          item => item.stoppedAt !== undefined
        );
        current.timer = setTimeout(
          () => {
            current.timer = undefined;
            void read();
          },
          finalCapture ? 0 : 2000
        );
      }
    }
  };
  if (!current.reading && !current.timer)
    current.timer = setTimeout(() => {
      current.timer = undefined;
      void read();
    }, 0);
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    scope.stoppedAt = diagnosticNow();
    if (current.timer) clearTimeout(current.timer);
    current.timer = undefined;
    // Detached final capture does not delay the flush's own result/error.
    if (!current.reading) void read();
  };
}
